import { CountryDataTimelineComponent } from "../countrydatatimeline/countrydatatimeline.component";
import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { ICountry } from "../../interfaces/icountry";
import { CountryDataStatus } from "../../enums/countrydatastatus";
import { DropDownItem } from "../../models/dropdown";
import { ICountryConfigSet } from "../../interfaces/icountryconfigset";
import { CountryDataService } from "../../services/countrydata.service";
import { ItemConfigService } from "../../services/itemconfig.service";
import { ILatestUpdateModal } from "../../interfaces/ilatestupdatemodal";
import { LatestUpdateService } from "../../services/latestupdate.service";
import { CountryDashboardDescriptionLatestUpdate } from "../../models/latestupdate";
import { LatestUpdateModalComponent } from "../latestupdatemodal/latestupdatemodal.component";
import { CommentsModalComponent } from "../commentsmodal/commentsmodal.component";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { WorkflowStageDataStatusComponent } from "../workflow/workflowstagedatastatus/workflowstagedatastatus.component";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";

@Component({
    selector: "country",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CountryDataTimelineComponent, LatestUpdateModalComponent, CommentsModalComponent, ConfirmationModalComponent, WorkflowStageDataStatusComponent],
    templateUrl: "./country.component.html",
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class CountryComponent implements OnInit, AfterViewInit {
    countryDataStatus: any = CountryDataStatus;
    @ViewChild("latestUpdateModal") public latestUpdateModal!: LatestUpdateModalComponent;
    @ViewChild("revertModal") public revertModal!: ConfirmationModalComponent;
    @ViewChild("commentsModal") public commentsModal!: CommentsModalComponent;
    itemConfigSet!: ICountryConfigSet;
    allCountries: ICountry[] = [];
    updateText: string = "";
    countries: DropDownItem[] = [];
    selectedCountry!: DropDownItem;
    activeCountry: DropDownItem[] = [];
    commentsModalState!: string;
    commentsHidden: boolean = false;

    constructor(private toastr: ToastrService,
        private itemConfigService: ItemConfigService,
        private activatedRoute: ActivatedRoute,
        private latestUpdateService: LatestUpdateService,
        private countryDataService: CountryDataService,
        private store: Store<IAppStore>,
        private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.itemConfigSet = this.activatedRoute.snapshot.data["country"];
        this.setLatestUpdateContent();
    }

    ngAfterViewInit(): void {
        // Set allowCorrections after view is initialized and ViewChild is available
        if (this.latestUpdateModal && this.itemConfigSet) {
            this.latestUpdateModal.allowCorrections = this.itemConfigSet.hasLiveData;
            this.cdr.detectChanges();
        }
    }

    /**
     * Gets called when the user confirms reverting of a draft version
     */
    onRevert(): void {
        this.countryDataService.deleteDraft(this.itemConfigSet.country.code).subscribe(() => {
            this.toastr.success("Draft version has been removed");
            this.itemConfigService.getConfigSetByCountry(this.itemConfigSet.country.code)
                .subscribe(cfgSet => {
                    this.itemConfigSet = cfgSet;
                    this.cdr.detectChanges();
            });
        });
    }

    /**
     * Gets called when the content has been approved
     */
    onApprove(comments: string, isCorrection: boolean = false): void {
        const canPublish: boolean = this.itemConfigSet.canPublish;
        this.itemConfigSet.comments = comments;
        this.itemConfigSet.isCorrection = isCorrection;
        this.countryDataService.submit(this.itemConfigSet).subscribe((data: ICountryConfigSet) => {
            this.itemConfigSet = data;
            if (!canPublish) {
                this.toastr.success("The content has been successfully sent for review");
            } else {
                this.toastr.success("The content has been successfully published");
                if (this.latestUpdateModal) {
                    this.latestUpdateModal.allowCorrections = true;
                }
            }
            this.cdr.detectChanges();
        });
    }

    /**
     * Gets called when the content has been rejected
     */
    onReject(reason: string): void {
        this.itemConfigSet.comments = reason;
        this.countryDataService.reject(this.itemConfigSet).subscribe((data: ICountryConfigSet) => {
            this.itemConfigSet = data;
            this.toastr.success("The content has been successfully rejected");
            this.cdr.detectChanges();
        });
    }

    onCommentsModalSubmit(comments: string): void {
        switch (this.commentsModalState) {
            case "reject":
                this.onReject(comments);
                break;
            case "approve":
                this.onApprove(comments);
                break;
            default:
                break;
        }
    }

    openModal(modal: any): void {
        modal.openModal();
    }

    /**
     * Publishes latest update to server
     * @param countryDescription
     */
    publishContent(latestUpdateContent: ILatestUpdateModal): void {
        this.onApprove("", latestUpdateContent.isCorrection);
        if (latestUpdateContent.latestUpdateAlert) {
            let defaultRoute: string = "";

            this.store.select(e => e.user.selectedBusinessFunction)
                .subscribe(e => defaultRoute = e.defaultRoute);

            const latestUpdate: CountryDashboardDescriptionLatestUpdate =
            new CountryDashboardDescriptionLatestUpdate(this.itemConfigSet.country.id,
                this.itemConfigSet.businessFunctionId,
                latestUpdateContent.description,
                this.itemConfigSet.country.code,
                defaultRoute,
                latestUpdateContent.sendEmail);
            this.latestUpdateService.create(latestUpdate).subscribe(() => {
                this.toastr.success("Latest updated published");
            });
        }
    }

    /**
     * Sets the latest update content for the text area
     */
    setLatestUpdateContent(): void {
        this.updateText = `A change was made to ${this.itemConfigSet.country.shortName}`;
    }

    /**
     * Click hook to display publish options
     */
    onPublishPrompt(): void {
        if (this.latestUpdateModal) {
            this.openModal(this.latestUpdateModal);
        }
    }

    /**
     * Click hook to prevent accidental revert
     */
    onRevertPrompt(): void {
        this.openModal(this.revertModal);
    }

    /**
     * Click hook to display publish options
     */
    onApprovePrompt(): void {
        this.commentsModalState = "approve";
        this.commentsModal.comments = "";
        this.commentsModal.submitButtonText = "Approve content";
        this.commentsModal.title = "Approve content";
        this.commentsModal.headerText = "Please provide comments to support this approval (optional)";
        this.commentsModal.required = false;
        this.openModal(this.commentsModal);
    }

    /**
     * Click hook to display rejection comments
     */
    onRejectPrompt(): void {
        this.commentsModalState = "reject";
        this.commentsModal.comments = "";
        this.commentsModal.submitButtonText = "Reject content";
        this.commentsModal.title = "Reject content";
        this.commentsModal.headerText = "Please provide reasons for rejection";
        this.commentsModal.required = true;
        this.openModal(this.commentsModal);
    }

    /**
     * Click hook to set country into draft mode
     */
    onCreateDraftPrompt(): void {
        this.countryDataService
            .createDraft(this.itemConfigSet).subscribe((res:ICountryConfigSet) => {
                    this.toastr.success("Draft version created");
                    this.itemConfigSet = res;
            });
    }

    /**
     * Click hook to toggle visibility on comments
     */
    toggleComments(): void {
      this.commentsHidden = !this.commentsHidden;
    }
}




